import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Portal } from '@sorare/core/src/atoms/layout/Portal/index.tsx';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop.ts';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader.ts';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { InviteCTA } from '#src/components/series/inviteFriends/InviteCTA/index.tsx';
import { InviteContent } from '#src/components/series/inviteFriends/InviteDialog/InviteContent/index.tsx';
import { RewardsContent } from '#src/components/series/inviteFriends/InviteDialog/RewardsContent/index.tsx';
import { useEnsureReferrerCode } from '#src/hooks/useEnsureReferrerCode.ts';

import type {
  SetReferralOnlineLandingQuery,
  SetReferralOnlineLandingQueryVariables,
} from './__generated__/page.graphql.ts';

const Root = styled(Vertical)`
  position: relative;
  padding-top: var(--double-unit);
  @media ${laptopAndAbove} {
    padding-top: calc(6 * var(--unit));
  }
`;
const BackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  & * {
    width: 100%;
    height: 100%;
  }
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 40%, var(--c-black) 100%);
  }
`;

const TabBarWrapper = styled.div`
  align-self: center;
`;
const ContentGrid = styled.div`
  min-height: calc(100vh - var(--current-stack-height));
  @media ${laptopAndAbove} {
    display: grid;
    grid-template-columns: 1fr 400px;
  }
`;
const LeftPanel = styled(Vertical).attrs({ gap: 4, center: true })`
  padding: var(--intermediate-unit) var(--double-unit);
`;
const RightPanel = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--intermediate-unit) var(--double-unit);
  @media ${laptopAndAbove} {
    border-left: 1px solid var(--c-nd-200);
  }
`;

const SET_REFERRAL_ONLINE_LANDING_QUERY = gql`
  query SetReferralOnlineLandingQuery($sport: Sport!) {
    cardSet {
      id
      maximumInviteTasksAsReferrer(sport: $sport)
      ...InviteContent_cardSetRoot
      ...RewardsContent_cardSetRoot
    }
    currentUser {
      slug
      referrerCode
      inviteTasksAsReferrer(sport: $sport) {
        id
      }
    }
  }
  ${InviteContent.fragments.cardSetRoot}
  ${RewardsContent.fragments.cardSetRoot}
` as TypedDocumentNode<
  SetReferralOnlineLandingQuery,
  SetReferralOnlineLandingQueryVariables
>;

export const loader = queryLoader(
  SET_REFERRAL_ONLINE_LANDING_QUERY,
  ({ sport }) => ({
    sport,
  })
);

const SetInvitePage = () => {
  const isDesktop = useIsLaptop();
  const [tab, setTab] = useState<'invite' | 'rewards'>('invite');

  const { data, loading } = useQueryLoader<SetReferralOnlineLandingQuery>();

  const { currentUser, cardSet } = data || {};
  const { maximumInviteTasksAsReferrer } = cardSet || {};
  const { inviteTasksAsReferrer, referrerCode } = currentUser || {};
  const { generating } = useEnsureReferrerCode(referrerCode, {
    skip: loading || !currentUser,
  });

  const friendsRemaining =
    (maximumInviteTasksAsReferrer || 10) - (inviteTasksAsReferrer?.length || 0);

  return (
    <Root>
      <Portal id="page-background">
        <BackgroundWrapper>
          <TextureBackground
            background="wave1"
            mode="dark"
            color="var(--c-set-surface-tertiary)"
          />
        </BackgroundWrapper>
      </Portal>
      <PageHeader text={navLabels.inviteFriends} disableBackBrowser />
      {!isDesktop && (
        <TabBarWrapper>
          <TabBar variant="primary" value={tab}>
            <Tab value="invite" onClick={() => setTab('invite')}>
              <FormattedMessage {...glossary.invite} />
            </Tab>
            <Tab value="rewards" onClick={() => setTab('rewards')}>
              <FormattedMessage {...glossary.rewards} />
            </Tab>
          </TabBar>
        </TabBarWrapper>
      )}
      <ContentGrid>
        {!!(isDesktop || tab === 'invite') && (
          <LeftPanel>
            <InviteContent cardSetRoot={cardSet} loading={loading}>
              <InviteCTA
                referrerCode={referrerCode}
                friendsRemaining={friendsRemaining}
                generating={generating}
              />
            </InviteContent>
          </LeftPanel>
        )}
        {!!(isDesktop || tab === 'rewards') && (
          <RightPanel>
            <Vertical gap={3} style={{ minHeight: '50vh' }}>
              <Vertical gap={1.5}>
                <LabelL bold>
                  <FormattedMessage {...glossary.rewards} />
                </LabelL>
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="Earn extra rewards as your friends progress" />
                </BodyS>
              </Vertical>
              <RewardsContent cardSetRoot={cardSet} />
            </Vertical>
          </RightPanel>
        )}
      </ContentGrid>
    </Root>
  );
};

export default SetInvitePage;
